import { fetchProvider } from './fetch';

type ActionName = 'Render';
type TimerType = 'Success' | 'Timeout';

interface MetricBase {
  productName: string;
  actionName: ActionName;
  componentName: string;
  componentLocation: string;
  siteDomain: string;
}

interface CounterMetric extends MetricBase {
  type: 'Init';
}
interface ErrorMetric extends MetricBase {
  type: 'Error';
  spendTime: number;
  errorType?: string;
}
interface TimerMetric extends MetricBase {
  type: TimerType;
  spendTime: number;
}

type AnyMetric = CounterMetric | ErrorMetric | TimerMetric;

const SET_INTERVAL_TIMER = 5000;
const METRICS_BASE_URL = '/loy/api/monitoring/v1/';
const METRICS_ENDPOINT = `${METRICS_BASE_URL}metrics`;

export const createMetricsTransport = () => {
  const httpClient = fetchProvider('autoshipment-central-hub-front', METRICS_BASE_URL);
  let metricsBuffer: AnyMetric[] = [];

  const pushMetricsToApi = async (): Promise<void> => {
    if (metricsBuffer.length === 0) return;
    const metrics = [...metricsBuffer];
    metricsBuffer = [];
    await httpClient.post('metrics', { metrics }).catch((e) => {
      console.error('Error pushing metrics', e);
      metricsBuffer.unshift(...metrics);
    });
  };

  const addToBuffer = (metric: AnyMetric): void => {
    metricsBuffer.push(metric);
    if (metricsBuffer.length >= 50) void pushMetricsToApi();
  };

  const metricsIntervalId = setInterval(() => {
    if (metricsBuffer.length) void pushMetricsToApi();
  }, SET_INTERVAL_TIMER);

  const flushOnPageHide = (event: PageTransitionEvent): void => {
    if (!event.persisted && metricsBuffer.length > 0) {
      const metrics = [...metricsBuffer];
      metricsBuffer = [];
      void fetch(METRICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Caller': 'autoshipment-central-hub-front',
        },
        body: JSON.stringify({ metrics }),
        keepalive: true,
      });
    }
  };

  window.addEventListener('pagehide', flushOnPageHide);

  const destroy = (): void => {
    window.removeEventListener('pagehide', flushOnPageHide);
    clearInterval(metricsIntervalId);
    void pushMetricsToApi();
  };

  return { addToBuffer, destroy };
};

export type MetricsTransport = ReturnType<typeof createMetricsTransport>;

export const createMetricsReporter = (
  transport: MetricsTransport,
  componentMeta: {
    componentName: string;
    componentLocation: string;
    productName: string;
    siteDomain: string;
  },
) => {
  const initTimestamp = Date.now();
  const getElapsedMillis = () => Date.now() - initTimestamp;
  const base = componentMeta;
  let successReported = false;

  return {
    renderInit: () => transport.addToBuffer({ type: 'Init', actionName: 'Render', ...base }),

    renderSuccess: () => {
      if (successReported) return;
      successReported = true;
      transport.addToBuffer({
        type: 'Success',
        actionName: 'Render',
        spendTime: getElapsedMillis(),
        ...base,
      });
    },

    renderError: (errorType?: string) =>
      transport.addToBuffer({
        type: 'Error',
        actionName: 'Render',
        spendTime: getElapsedMillis(),
        errorType,
        ...base,
      }),
  };
};
